import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ShieldAlert, Eraser } from "lucide-react";

export default function DangerZone({ onCleared }) {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const clear = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete("/admin/rework-data");
      toast.success(`Rework data cleared — ${data.removed.reworks} request${data.removed.reworks === 1 ? "" : "s"} removed`);
      setOpen(false); setConfirm("");
      onCleared?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not clear data");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-[#8a3a2a]/30 bg-white rounded-sm" data-testid="danger-zone">
      <div className="px-5 py-4 border-b border-[#8a3a2a]/20 flex items-center gap-3">
        <ShieldAlert size={16} className="text-[#8a3a2a]" />
        <div className="font-display font-bold text-[#232826]">Recovery tools</div>
        <div className="ml-auto text-[10px] uppercase tracking-widest text-[#a39f98] font-mono hidden sm:block">Recovery account only</div>
      </div>
      <div className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1 text-sm text-[#6b6a68]">
          Permanently deletes every rework request, comment and notification. Users, roles and Teams settings are kept.
          Use this to reset after testing before go-live.
        </div>
        <Button type="button" variant="outline" onClick={() => setOpen(true)} data-testid="clear-rework-data"
          className="rounded-sm border-[#8a3a2a]/50 text-[#8a3a2a] hover:bg-[#8a3a2a]/10 hover:text-[#6a2a1e] uppercase text-xs tracking-widest">
          <Eraser size={13} className="mr-2" /> Clear rework data
        </Button>
      </div>
      <AlertDialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setConfirm(""); }}>
        <AlertDialogContent className="rounded-sm border-[#e6e5df] bg-white" data-testid="clear-data-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-[#232826]">Clear all rework data?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#6b6a68]">
              This cannot be undone. Type <span className="font-mono font-bold text-[#232826]">CLEAR</span> to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="CLEAR" data-testid="clear-data-confirm-input"
            className="rounded-sm border-[#cfcfc9] font-mono" autoFocus />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="clear-data-cancel" disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); clear(); }} disabled={busy || confirm !== "CLEAR"} data-testid="clear-data-confirm"
              className="rounded-sm bg-[#8a3a2a] text-white hover:bg-[#6a2a1e]">
              {busy ? "Clearing…" : "Delete everything"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
